import { isRecord, requestJson } from "./http.js";
import type { AccountRole } from "./auth.js";

export type AccountStatus = "active" | "pending" | "expired" | "disabled";

export interface AdminUser {
  readonly id: string;
  readonly email: string;
  readonly role: AccountRole;
  readonly status: AccountStatus;
  readonly createdAt: number;
  readonly lastLoginAt: number | null;
  readonly passwordChangedAt: number | null;
  readonly tempPasswordExpiresAt: number | null;
  readonly twoFactorEnabled: boolean;
  /** Enrolled in admin-assisted recovery under the current instance key. */
  readonly recoveryEnrolled: boolean;
  /** Reset by an admin and still on the one-time password. */
  readonly recoveryReset: boolean;
}

export interface SystemAuditEvent {
  readonly id: string;
  readonly action: string;
  readonly actorId: string | null;
  readonly actorEmail: string | null;
  readonly targetId: string | null;
  readonly targetEmail: string | null;
  readonly createdAt: number;
  readonly hash: string;
}

export type MailTransport = "console" | "smtp" | "resend" | "off";

export interface MailStatus {
  readonly transport: MailTransport;
  /** Sender address; null when sending is off. */
  readonly from: string | null;
}

export type MailTestResult =
  | { readonly ok: true; readonly to: string }
  | { readonly ok: false; readonly message: string };

const TRANSPORTS: ReadonlyArray<MailTransport> = ["console", "smtp", "resend", "off"];

export function isMailStatus(value: unknown): value is MailStatus {
  return isRecord(value) &&
    TRANSPORTS.some((t: MailTransport): boolean => t === value["transport"]) &&
    (value["from"] === null || typeof value["from"] === "string");
}

const STATUSES: ReadonlyArray<AccountStatus> = ["active", "pending", "expired", "disabled"];

function isNullableNumber(value: unknown): value is number | null {
  return value === null || typeof value === "number";
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

export function isAdminUser(value: unknown): value is AdminUser {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["email"] === "string" &&
    (value["role"] === "admin" || value["role"] === "user") &&
    STATUSES.some((s: AccountStatus): boolean => s === value["status"]) &&
    typeof value["createdAt"] === "number" &&
    isNullableNumber(value["lastLoginAt"]) &&
    isNullableNumber(value["passwordChangedAt"]) &&
    isNullableNumber(value["tempPasswordExpiresAt"]) &&
    typeof value["twoFactorEnabled"] === "boolean" &&
    typeof value["recoveryEnrolled"] === "boolean" &&
    typeof value["recoveryReset"] === "boolean";
}

export function isSystemAuditEvent(value: unknown): value is SystemAuditEvent {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["action"] === "string" &&
    isNullableString(value["actorId"]) &&
    isNullableString(value["actorEmail"]) &&
    isNullableString(value["targetId"]) &&
    isNullableString(value["targetEmail"]) &&
    typeof value["createdAt"] === "number" &&
    typeof value["hash"] === "string";
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

function userFrom(body: unknown): AdminUser {
  if (!isRecord(body) || !isAdminUser(body["user"])) {
    throw unexpected();
  }
  return body["user"];
}

export async function listUsers(): Promise<AdminUser[]> {
  const body = await requestJson("/api/v1/admin/users");
  if (!isRecord(body) || !Array.isArray(body["users"])) {
    throw unexpected();
  }
  const out: AdminUser[] = [];
  for (const user of body["users"]) {
    if (!isAdminUser(user)) {
      throw unexpected();
    }
    out.push(user);
  }
  return out;
}

/** saltB64/authKeyB64 come from the one-time password the admin's browser generated. */
export async function createUser(input: { email: string; role: AccountRole; saltB64: string; authKeyB64: string }): Promise<AdminUser> {
  return userFrom(await requestJson("/api/v1/admin/users", { method: "POST", body: input }));
}

export async function resetTempPassword(id: string, saltB64: string, authKeyB64: string): Promise<AdminUser> {
  return userFrom(await requestJson(`/api/v1/admin/users/${id}/temp-password`, { method: "POST", body: { saltB64, authKeyB64 } }));
}

export async function patchUser(id: string, patch: { role?: AccountRole; disabled?: boolean }): Promise<AdminUser> {
  return userFrom(await requestJson(`/api/v1/admin/users/${id}`, { method: "PATCH", body: patch }));
}

export async function revokeSessions(id: string): Promise<AdminUser> {
  return userFrom(await requestJson(`/api/v1/admin/users/${id}/logout`, { method: "POST", body: {} }));
}

/** Returns the names of teams that were deleted together with the account. */
export async function deleteUser(id: string): Promise<ReadonlyArray<string>> {
  const body = await requestJson(`/api/v1/admin/users/${id}`, { method: "DELETE" });
  if (!isRecord(body) || body["ok"] !== true || !Array.isArray(body["deletedTeams"])) {
    throw unexpected();
  }
  return body["deletedTeams"].filter((t: unknown): t is string => typeof t === "string");
}

export async function fetchMailStatus(): Promise<MailStatus> {
  const body = await requestJson("/api/v1/admin/mail");
  if (!isMailStatus(body)) {
    throw unexpected();
  }
  return body;
}

/** Sends a test mail to the signed-in admin. A delivery failure is a result, not an exception. */
export async function sendTestMail(): Promise<MailTestResult> {
  const body = await requestJson("/api/v1/admin/mail/test", { method: "POST", body: {} });
  if (isRecord(body) && body["ok"] === true && typeof body["to"] === "string") {
    return { ok: true, to: body["to"] };
  }
  if (isRecord(body) && body["ok"] === false && typeof body["message"] === "string") {
    return { ok: false, message: body["message"] };
  }
  throw unexpected();
}

export async function listSystemAudit(): Promise<SystemAuditEvent[]> {
  const body = await requestJson("/api/v1/admin/audit?limit=200");
  if (!isRecord(body) || !Array.isArray(body["events"])) {
    throw unexpected();
  }
  const out: SystemAuditEvent[] = [];
  for (const event of body["events"]) {
    if (!isSystemAuditEvent(event)) {
      throw unexpected();
    }
    out.push(event);
  }
  return out;
}
